import type {
  ContestPositionMap,
  ConferenceContest,
  ConferenceOption,
  ContestContent,
  OptionContent,
  E2E,
} from '@/types';

type ValidationPolicy = Record<string, any>;

interface NestedValidationPolicy {
  undervoteBetween?: { behavior: string; feedbackScreen: string; feedbackType: string };
  undervoteBelowMin?: { behavior: string; feedbackScreen: string; feedbackType: string; editable: boolean };
  overvote?: { behavior: string; feedbackScreen: string; feedbackType: string };
  blankVoteFeedback?: { enabled: boolean; feedbackScreen: string; feedbackType: string; message?: Record<string, string>; inlineMessage?: Record<string, string> };
}

const formatValidationPolicy = (raw: ValidationPolicy | undefined): NestedValidationPolicy | undefined => {
  if (!raw || Object.keys(raw).length === 0) return undefined;

  const policy: NestedValidationPolicy = {};

  if (raw.overvote_behavior) {
    policy.overvote = {
      behavior: raw.overvote_behavior,
      feedbackScreen: raw.overvote_feedback_screen || "ballot_page",
      feedbackType: raw.overvote_feedback_type || "on_screen_message",
    };
  }

  if (raw.undervote_between_behavior) {
    policy.undervoteBetween = {
      behavior: raw.undervote_between_behavior,
      feedbackScreen: raw.undervote_between_feedback_screen || "ballot_page",
      feedbackType: raw.undervote_between_feedback_type || "on_screen_message",
    };
  }

  if (raw.undervote_below_min_behavior) {
    policy.undervoteBelowMin = {
      behavior: raw.undervote_below_min_behavior,
      feedbackScreen: raw.undervote_below_min_feedback_screen || "ballot_page",
      feedbackType: raw.undervote_below_min_feedback_type || "on_screen_message",
      editable: false,
    };
  }

  if (raw.blank_vote_feedback_enabled === true || raw.blank_vote_feedback_enabled === "true") {
    const blankFeedback: NestedValidationPolicy["blankVoteFeedback"] = {
      enabled: true,
      feedbackScreen: raw.blank_vote_feedback_screen || "ballot_page",
      feedbackType: raw.blank_vote_feedback_type || "on_screen_message",
    };
    if (raw.blank_vote_feedback_message) {
      blankFeedback.message = raw.blank_vote_feedback_message;
    }
    if (raw.blank_vote_feedback_inline_message) {
      blankFeedback.inlineMessage = raw.blank_vote_feedback_inline_message;
    }
    policy.blankVoteFeedback = blankFeedback;
  }

  return Object.keys(policy).length > 0 ? policy : undefined;
};

const formatContest = (contest: any): ContestContent & { optionsCount: number } => {
  const options: OptionContent[] = contest?.options?.map((option: ConferenceOption) => {
    if (option.parent === null) return formatOption(option);
  }).filter(Boolean) ?? [];

  const formatted: ContestContent & { optionsCount: number; showVoterWeightMessage?: boolean } = {
    reference: contest.reference,
    title: contest.title,
    mode: contest.mode,
    options: options,
    optionsCount: contest.optionsCount,
    customRulesets: contest.customRulesets,
    disregardVoterWeight: contest.disregardWeights,
    showVoterWeightMessage: contest.showVoterWeightMessage,
    displayDescriptionOnSummary: contest.displayDescriptionOnSummary,
    multipleVotingInterface: contest.multipleVotingInterface || "crosses",
    markingType: {
      votesAllowedPerOption: contest.votesAllowedPerOption,
      blankSubmission: contest.blankOption,
      minMarks: contest.minimumVotes,
      maxMarks: contest.maximumVotes,
      maxPiles: contest.maxPiles,
      voteVariation: contest.ruleSet,
      encoding: {
        codeSize: 1,
        maxSize: 1,
        cryptogramCount: 1
      }
    },
    resultType: {
      name: contest.resultMethod
    },
  };

  if (contest.question) formatted.question = contest.question;
  if (contest.description) formatted.description = contest.description;
  if (contest.collapsable) formatted.collapsable = contest.collapsable;
  if (contest.collapseDefault) formatted.collapseDefault = contest.collapseDefault;
  if (contest.searchForm) formatted.searchForm = contest.searchForm;
  if (contest.randomizeOptions) formatted.randomizeOptions = contest.randomizeOptions;
  if (contest.blankOptionColor) formatted.blankOptionColor = contest.blankOptionColor;
  if (contest.quadraticVoting) formatted.markingType.quadraticVoting = contest.quadraticVoting;
  formatted.markingType.quadraticVotingVoiceCredits = contest.quadraticVotingVoiceCredits || 0;

  const validationPolicy = formatValidationPolicy(contest.validationPolicy);
  if (validationPolicy) (formatted as any).validationPolicy = validationPolicy;

  return formatted;
}

const formatOption = (option: ConferenceOption): OptionContent => {
  const parsedOption: OptionContent = {
    reference: option.reference,
    code: option.code,
    title: option.title,
  };

  if (option.id) parsedOption.id = option.id;
  if (option.description) parsedOption.description = option.description;
  if (option.image) parsedOption.image = option.image;
  if (option.selectable !== undefined) parsedOption.selectable = option.selectable;
  if (option.exclusive !== undefined) parsedOption.exclusive = option.exclusive;
  if (option.excluded !== undefined) parsedOption.excluded = option.excluded;
  if (option.position) parsedOption.position = option.position;
  if (option.randomizeChildren !== undefined) parsedOption.randomizeChildren = option.randomizeChildren;
  if (option.accentColor) parsedOption.accentColor = option.accentColor;
  if (option.url) parsedOption.url = option.url;
  if (option.videoUrl) parsedOption.videoUrl = option.videoUrl;
  if (option.minChooseableSuboptions) parsedOption.minChooseableSuboptions = option.minChooseableSuboptions;
  if (option.maxChooseableSuboptions) parsedOption.maxChooseableSuboptions = option.maxChooseableSuboptions;
  if (option.voteLimit) parsedOption.voteLimit = option.voteLimit;
  if (option.writeIn) {
    parsedOption.writeIn = {
      maxSize: option.writeInMaxSize,
      encoding: "utf8",
      placeholder: option.writeInPlaceholder,
    }
  }
  if (option.children) parsedOption.children = option.children.map(child => formatOption(child));
  if (option.candidateId) parsedOption.candidateId = option.candidateId;

  return parsedOption;
}

const orderContests = (contestsSelection: ConferenceContest[], contestPositions: ContestPositionMap): ConferenceContest[] => {
  return contestsSelection.sort((a, b) =>
    contestPositions[a.reference] - contestPositions[b.reference]
  );
}

const orderResults = (contestsSelection: E2E.ContestResult[], contestPositions: ContestPositionMap): E2E.ContestResult[] => {
  return contestsSelection.sort((a, b) =>
    contestPositions[a.contestReference] - contestPositions[b.contestReference]
  );
}

const flattenOptions = (tree: ConferenceOption[]): ConferenceOption[] => {
  return tree.flatMap((option: ConferenceOption) => {
    if (option.children) return [option, ...flattenOptions(option.children)];
    return [option];
  });
}

export { formatContest, orderContests, flattenOptions, orderResults };
